import { ReactElement, useMemo, useRef } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { Form } from '@appsumo/dorado-react';
import { PaymentElement } from '@stripe/react-stripe-js';

import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import { PaymentOptionTypes } from '~/types/checkout';
import { ADDITIONAL_PAYMENT_METHODS, STEP } from '~/constants/checkout';

import { paymentIcons } from '~/public/paymentIcons';

const { NEW_PAYMENT_METHOD, PAYPAL_PAYMENT_METHOD } =
  ADDITIONAL_PAYMENT_METHODS;

export default function PaymentOption({
  type,
  cardDetails,
  address,
  canPayWithCredits,
  defaultPaymentMethod,
  isPlusPaymentMethod,
  isLastPaymentOption,
}: PaymentOptionTypes): ReactElement {
  const radioRef = useRef<HTMLInputElement | null>(null);
  const { selectedPaymentMethod } = useCheckoutState();
  const { setStepErrorMessage, setPaymentElementData } = useCheckoutAPI();

  const containerClass = clsx('flex cursor-pointer flex-col py-6', {
    'border-b border-sundog':
      type !== PAYPAL_PAYMENT_METHOD && !isLastPaymentOption,
  });
  const childClass = clsx('flex w-full ', {
    'items-center': type !== NEW_PAYMENT_METHOD,
  });
  let icon = null;
  let title = 'Use a new payment method';
  let isDefaultChecked = defaultPaymentMethod === type;

  if (type === 'existing-card') {
    title = `Card ending in ${cardDetails.last4}`;
    isDefaultChecked = cardDetails.id === defaultPaymentMethod;

    const brand = (cardDetails.brand || '').toLowerCase();
    icon = paymentIcons[brand] || paymentIcons.defaultCard;
  } else if (type === PAYPAL_PAYMENT_METHOD) {
    title = 'Checkout with PayPal';
    icon = paymentIcons.paypal;
  }

  const handleRadioClick = (): void => {
    radioRef?.current?.click();
  };

  const plusBadgeUI = useMemo(() => {
    if (type === 'existing-card' && isPlusPaymentMethod) {
      return (
        <span className="h-[29px] rounded bg-ready-red/10 px-2 py-1 font-medium text-ready-red">
          Plus membership
        </span>
      );
    }
    return null;
  }, [type, isPlusPaymentMethod]);

  return (
    <div
      className={containerClass}
      onClick={handleRadioClick}
      onKeyDown={handleRadioClick}
      role="button"
      tabIndex={0}
    >
      <div className={childClass}>
        <Form.RadioButton
          name="paymentMethod"
          value={cardDetails?.id || type}
          defaultChecked={isDefaultChecked}
          ref={radioRef}
        />

        {icon && (
          <Image
            src={icon}
            width={40}
            height={24}
            alt="payment-method-icon"
            className="mx-2"
          />
        )}

        <div
          className={`flex w-full flex-col ${clsx({
            'w-full': type === NEW_PAYMENT_METHOD,
          })}`}
        >
          <div className="flex items-center justify-between">
            <p>{title}</p>

            <div className="hidden md:flex">{plusBadgeUI}</div>
          </div>
          {type === NEW_PAYMENT_METHOD && (
            <PaymentElement
              onChange={(data) => {
                setPaymentElementData(data);
                setStepErrorMessage(STEP.PAYMENT, '');
              }}
              options={{
                defaultValues: {
                  billingDetails: {
                    address,
                  },
                },
              }}
              className={`mt-4 ${clsx({
                hidden:
                  selectedPaymentMethod !== NEW_PAYMENT_METHOD ||
                  canPayWithCredits,
              })}`}
            />
          )}
        </div>
      </div>

      <div className="mt-2 ml-10 md:hidden">{plusBadgeUI}</div>
    </div>
  );
}
